import { type FooterColumn, isNavGroup, type NavEntry, type NavLink } from "./nav-model"

/**
 * Chinese labels for the header and footer menus (D-WEB-LANG-SCOPE). One entry per English href:
 * `nav-model.zh.test.ts` fails when a menu link has no Chinese text. Terms follow
 * docs/design/landing/copy-zh.md; ATO, ABN, GST, HECS-HELP, BAS and Logbook stay in Latin letters
 * because that is how they appear on ATO pages and in the apps.
 */
export const NAV_ZH_LINKS: Record<string, { label: string; description?: string }> = {
  "/mileage-tracker": {
    label: "里程记录与 Logbook",
    description: "GPS 行程、cents per km 与 logbook 法",
  },
  "/receipt-scanner": { label: "收据扫描", description: "拍下收据，自动读出总额和 GST" },
  "/invoicing": { label: "开发票", description: "发送 Tax Invoice，看谁已付款" },
  "/cents-per-km-calculator": {
    label: "cents per km 计算器",
    description: "本年度 ATO 费率与你的扣除额",
  },
  "/pay-calculator": {
    label: "税后工资计算器",
    description: "扣除税款、Medicare 和 HELP 后的到手收入",
  },
  "/hecs-repayment-calculator": {
    label: "HECS-HELP 还款计算器",
    description: "按收入算出强制还款额",
  },
  "/medicare-levy-calculator": {
    label: "Medicare levy 计算器",
    description: "Medicare levy 与低收入减免",
  },
  "/ato-logbook-template": { label: "ATO Logbook 模板", description: "免费的 CSV 和 PDF 行车日志" },
  "/invoice-generator": { label: "发票生成器", description: "无需账户，生成 PDF 发票" },
  "/gst-calculator": { label: "GST 计算器", description: "给价格加上或去掉 10% GST" },
  "/logbook-method": {
    label: "Logbook 法与 cents per km",
    description: "logbook 法什么时候更划算",
  },
  "/guides/ato-logbook-requirements": {
    label: "ATO Logbook 要求",
    description: "12 周里要记录什么",
  },
  "/guides/ato-cents-per-km-rate-history": {
    label: "历年 ATO cents per km 费率",
    description: "2022–23 起的每一档费率，附来源",
  },
  "/guides/tax-invoice-requirements": {
    label: "Tax Invoice 要求",
    description: "Tax Invoice 必须写明什么",
  },
  "/rideshare-drivers": { label: "网约车和外卖司机", description: "ABN、GST 与车辆开支" },
  "/glossary": { label: "术语表", description: "税务与 logbook 术语的通俗解释" },
  "/blog": { label: "博客", description: "新闻与费率变动" },
  "/guides": { label: "全部指南", description: "所有指南和免费工具" },
  "/compare/driversnote-alternative": {
    label: "Kestly 对比 Driversnote",
    description: "免费版、价格和功能，均附来源",
  },
  "/compare/spreadsheet-logbook": {
    label: "电子表格还是应用？",
    description: "12 周记录什么时候用电子表格就够",
  },
  "/compare": { label: "全部对比", description: "里程和收据应用的免费版一览" },
  "/pricing": { label: "价格" },
  "/how-it-works": { label: "使用方法" },
  "/faq": { label: "常见问题" },
  "/contact": { label: "联系我们" },
  "/trust": { label: "安全与隐私" },
  "/legal/privacy": { label: "隐私政策（英文）" },
  "/legal/terms-and-conditions": { label: "条款与条件（英文）" },
  "/legal/delete-account": { label: "删除账户（英文）" },
}

export const NAV_ZH_GROUPS: Record<string, string> = {
  Products: "产品",
  "Free tools": "免费工具",
  Guides: "指南",
  Product: "产品",
  Compare: "对比",
  Company: "公司",
}

const link = <T extends NavLink>(l: T): T => {
  const zh = NAV_ZH_LINKS[l.href]
  if (!zh) return l
  return { ...l, label: zh.label, ...(l.description ? { description: zh.description } : {}) }
}

export const localizeNavEntries = (entries: NavEntry[]): NavEntry[] =>
  entries.map((e) =>
    isNavGroup(e)
      ? { ...e, label: NAV_ZH_GROUPS[e.label] ?? e.label, items: e.items.map(link) }
      : link(e),
  )

export const localizeFooterColumns = (columns: FooterColumn[]): FooterColumn[] =>
  columns.map((c) => ({
    heading: NAV_ZH_GROUPS[c.heading] ?? c.heading,
    links: c.links.map(link),
  }))

export const localizeLegalLinks = (links: NavLink[]): NavLink[] => links.map(link)

export const localizeLinks = (links: NavLink[]): NavLink[] => links.map(link)
